import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { X, Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { KIND_LABEL } from './ClauseSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ['millwright', 'fabrication'];

const ClauseRow = ({ c, onChange, onDelete }) => (
  <tr className="border-t align-top" data-testid={`lib-row-${c.id}`}>
    <td className="py-1.5 pr-2 w-36"><Input value={c.category} onChange={(e) => onChange({ category: e.target.value })} className="h-8 text-xs" /></td>
    <td className="py-1.5 pr-2"><Textarea value={c.text} onChange={(e) => onChange({ text: e.target.value })} rows={1} className="min-h-[32px] py-1 text-sm resize-none" data-testid={`lib-text-${c.id}`} /></td>
    <td className="py-1.5 pr-2 text-center"><input type="checkbox" checked={!!c.default_on} onChange={(e) => onChange({ default_on: e.target.checked })} title="On by default for new quotes" data-testid={`lib-default-${c.id}`} /></td>
    {TYPES.map((t) => (
      <td key={t} className="py-1.5 pr-2 text-center"><input type="checkbox" checked={(c.quote_types || []).includes(t)} onChange={(e) => onChange({ quote_types: e.target.checked ? [...(c.quote_types || []), t] : (c.quote_types || []).filter((x) => x !== t) })} /></td>
    ))}
    <td className="py-1.5 pr-2 text-center"><input type="checkbox" checked={!!c.active} onChange={(e) => onChange({ active: e.target.checked })} title="Untick to retire (hidden from pickers)" data-testid={`lib-active-${c.id}`} /></td>
    <td className="py-1.5"><button onClick={onDelete} className="p-1 text-slate-500 hover:text-red-600" data-testid={`lib-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button></td>
  </tr>
);

/** Estimating-manager tool: standard clause library + page-2 Standard Terms & Conditions. */
export default function QuoteLibraryAdmin({ onClose }) {
  const [tab, setTab] = useState('clarification');
  const [items, setItems] = useState([]);
  const [terms, setTerms] = useState(null);
  const [dirtyIds, setDirtyIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [newText, setNewText] = useState('');
  const [newCat, setNewCat] = useState('');

  useEffect(() => {
    axios.get(`${API}/quote-doc/library`, { params: { include_inactive: true } }).then((r) => setItems(r.data)).catch(() => toast.error('Could not load the library'));
    axios.get(`${API}/quote-doc/standard-terms`).then((r) => setTerms(r.data.items)).catch(() => {});
  }, []);

  const change = (id, p) => { setItems((s) => s.map((c) => (c.id === id ? { ...c, ...p } : c))); setDirtyIds((s) => new Set([...s, id])); };
  const saveClauses = async () => {
    setSaving(true);
    try {
      await Promise.all([...dirtyIds].map((id) => { const c = items.find((x) => x.id === id); return c && axios.put(`${API}/quote-doc/library/${id}`, c); }));
      setDirtyIds(new Set()); toast.success('Library saved');
    } catch (e) { toast.error('Some changes did not save'); } finally { setSaving(false); }
  };
  const add = async () => {
    if (!newText.trim()) return;
    try {
      const r = await axios.post(`${API}/quote-doc/library`, { kind: tab, category: newCat.trim() || 'General', text: newText.trim(), quote_types: TYPES });
      setItems((s) => [...s, r.data]); setNewText(''); setNewCat('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this line from the library? Quotes that already use it keep their copy.')) return;
    await axios.delete(`${API}/quote-doc/library/${id}`);
    setItems((s) => s.filter((c) => c.id !== id));
  };
  const saveTerms = async () => {
    setSaving(true);
    try { const r = await axios.put(`${API}/quote-doc/standard-terms`, { items: terms }); setTerms(r.data.items); toast.success('Standard Terms & Conditions saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); } finally { setSaving(false); }
  };

  const list = items.filter((c) => c.kind === tab);
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="quote-library-admin">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <h3 className="font-semibold text-slate-900">Quote Library</h3>
            <p className="text-xs text-slate-500">Standard clarifications, inclusions and exclusions every estimator can tick on — plus the page-2 Standard Terms.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="lib-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 pt-3 flex gap-1 border-b">
          {[...Object.keys(KIND_LABEL), 'terms'].map((k) => (
            <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm rounded-t border-b-2 ${tab === k ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`} data-testid={`lib-tab-${k}`}>
              {k === 'terms' ? 'Standard T&Cs' : KIND_LABEL[k]}{k !== 'terms' && <span className="ml-1 text-xs text-slate-500">{items.filter((c) => c.kind === k).length}</span>}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {tab !== 'terms' ? (
            <>
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase text-slate-500 text-left"><th>Category</th><th>Wording</th><th className="text-center">Default</th><th className="text-center">MW</th><th className="text-center">Fab</th><th className="text-center">Active</th><th /></tr></thead>
                <tbody>{list.map((c) => <ClauseRow key={c.id} c={c} onChange={(p) => change(c.id, p)} onDelete={() => del(c.id)} />)}</tbody>
              </table>
              <div className="flex gap-2 mt-4 border-t pt-3">
                <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Category" className="h-9 w-40" data-testid="lib-new-category" />
                <Textarea value={newText} onChange={(e) => setNewText(e.target.value)} placeholder={`New standard ${tab}…`} rows={1} className="min-h-[36px] flex-1" data-testid="lib-new-text" />
                <Button variant="outline" onClick={add} disabled={!newText.trim()} data-testid="lib-new-add"><Plus className="h-4 w-4 mr-1" />Add</Button>
              </div>
            </>
          ) : terms === null ? <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div> : (
            <div className="space-y-3">
              {terms.map((t, i) => (
                <div key={i} className="flex gap-2 items-start" data-testid={`terms-row-${i}`}>
                  <Input value={t.title} onChange={(e) => setTerms((s) => s.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} placeholder="Title" className="h-9 w-56" />
                  <Textarea value={t.text} onChange={(e) => setTerms((s) => s.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} rows={2} className="flex-1 text-sm" />
                  <button onClick={() => setTerms((s) => s.filter((_, j) => j !== i))} className="p-1 mt-1 text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <Button variant="outline" onClick={() => setTerms((s) => [...s, { title: '', text: '' }])} data-testid="terms-add"><Plus className="h-4 w-4 mr-1" />Term</Button>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {tab !== 'terms'
            ? <Button onClick={saveClauses} disabled={saving || dirtyIds.size === 0} className="bg-blue-600 hover:bg-blue-700" data-testid="lib-save"><Save className="h-4 w-4 mr-1" />Save changes{dirtyIds.size ? ` (${dirtyIds.size})` : ''}</Button>
            : <Button onClick={saveTerms} disabled={saving || terms === null} className="bg-blue-600 hover:bg-blue-700" data-testid="terms-save"><Save className="h-4 w-4 mr-1" />Save T&amp;Cs</Button>}
        </div>
      </div>
    </div>
  , document.body);
}
